import React, { useMemo } from "react";
import styled from "styled-components";
import { colors2 } from "oolib";
import { UI_BODY_SEMIBOLD, UI_BODY_SEMIBOLD_SM, UI_CAPTION, UI_CAPTION_DF } from "oolib/dist/v2/components/Typo2";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";

/**
 * "Act first": the field's items ranked by severity × momentum × volume,
 * top N, beside the field. Momentum here is the field's own x-axis number
 * (the index: relative change × confidence, in band widths), volume the
 * observation count; a cooling sub-topic counts as momentum 0, not as a
 * discount, so a severe, large, steady problem still ranks. Click a row to
 * open the sub-topic.
 */
const TOP_N = 8;
const scoreOf = (item) => {
  const index = item.momentum?.available ? item.momentum.index || 0 : 0;
  return item.severity.value * (1 + Math.max(0, index)) * Math.log10((item.n || 0) + 1);
};
// The list's items, ranked and cut — the field reads it too, to keep these
// bubbles at full strength and fade the rest.
export const rankActFirst = (items) =>
  [...items].map((item) => ({ item, score: scoreOf(item) })).sort((a, b) => b.score - a.score).slice(0, TOP_N).map(({ item }) => item);
const signed = (n, digits = 2) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(digits)}`;

// 360px beside the field on the page; in a narrow report widget it gives
// way down to a readable floor rather than forcing a horizontal scroll.
const Panel = styled.div`
  flex: 0 1 360px;
  min-width: 240px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors2.grey10};
  border-radius: 12px;
  background: ${colors2.white};
`;
const Head = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid ${colors2.grey10};
`;
const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 8px 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;
const Row = styled.li`
  display: grid;
  grid-template-columns: 24px 12px 1fr;
  column-gap: 8px;
  align-items: baseline;
  padding: 10px 20px;
  cursor: pointer;

  &:hover {
    background: ${colors2.grey2};
  }
`;
const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  transform: translateY(-1px);
`;
const Foot = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${colors2.grey10};
`;

export const ActFirstPanel = ({ items, plottedCount, totalCount, onRowClick }) => {
  const categoryTheme = useSignalCategories();
  const ranked = useMemo(() => rankActFirst(items), [items]);
  return (
    <Panel>
      <Head>
        <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>Act first</UI_BODY_SEMIBOLD>
        <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginTop: 2 }}>Ranked by severity × momentum × volume</UI_CAPTION>
      </Head>
      <List>
        {ranked.map((item, i) => {
          const index = item.momentum?.available ? item.momentum.index || 0 : null;
          return (
            <Row key={item.nodeId} onClick={() => onRowClick?.(item.row)}>
              <UI_CAPTION_DF style={{ color: colors2.grey50 }}>{String(i + 1).padStart(2, "0")}</UI_CAPTION_DF>
              <Dot $color={item.category ? categoryTheme.accentOf(item.category) : colors2.grey60} />
              <div style={{ minWidth: 0 }}>
                <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.label}
                </UI_BODY_SEMIBOLD_SM>
                <UI_CAPTION_DF style={{ color: colors2.grey50, display: "block", marginTop: 2 }}>
                  sev {item.severity.value.toFixed(1)} · mom {index === null ? "–" : signed(index)} · {(item.n || 0).toLocaleString()} obs
                </UI_CAPTION_DF>
              </div>
            </Row>
          );
        })}
        {!ranked.length && (
          <UI_CAPTION style={{ color: colors2.grey60, display: "block", padding: "10px 20px" }}>Nothing to rank in this zone.</UI_CAPTION>
        )}
      </List>
      <Foot>
        <UI_CAPTION style={{ color: colors2.grey60 }}>
          {plottedCount} of {totalCount} issues plotted
        </UI_CAPTION>
      </Foot>
    </Panel>
  );
};
